import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type {
  TenantSettings,
  UpdateTenantSettingsDto,
  GenerateQRSecretResponse,
  TenantOrganizationInfo,
} from '../../types/tenant.types';

// Query key factory
export const settingsKeys = {
  all: ['tenant-settings'] as const,
  settings: () => [...settingsKeys.all, 'current'] as const,
  organization: () => [...settingsKeys.all, 'organization'] as const,
};

// Get tenant settings
export const useTenantSettings = () => {
  return useQuery({
    queryKey: settingsKeys.settings(),
    queryFn: async () => {
      const response = await apiClient.getTenantSettings();
      return response as TenantSettings;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Update tenant settings
export const useUpdateTenantSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTenantSettingsDto) => {
      const response = await apiClient.updateTenantSettings(data);
      return response as TenantSettings;
    },
    onSuccess: (data) => {
      // Invalidate and refetch settings
      queryClient.setQueryData(settingsKeys.settings(), data);
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
};

// Generate new QR secret
export const useGenerateQRSecret = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.generateQRSecret();
      return response as GenerateQRSecretResponse;
    },
    onSuccess: (data) => {
      // Update cached settings with new secret
      queryClient.setQueryData(settingsKeys.settings(), (old: TenantSettings | undefined) => {
        if (!old) return old;
        return {
          ...old,
          qr_secret: data.qr_secret,
        };
      });
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
};

// Get organization information
export const useOrganizationInfo = () => {
  return useQuery({
    queryKey: settingsKeys.organization(),
    queryFn: async () => {
      const response = await apiClient.getOrganizationInfo();
      return response as TenantOrganizationInfo;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (дольше, так как это редко меняется)
  });
};
